package com.speedsoftware.nibbler.ui.auth

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.ui.AppState
import com.speedsoftware.nibbler.ui.theme.NibblerColors

@Composable
fun ForgotPasswordScreen(appState: AppState, token: String? = null) {
    val scope = rememberCoroutineScope()
    val flow = remember { ForgotPasswordFlow(appState, scope, token) }

    var code by remember { mutableStateOf("") }
    var newPassword by remember { mutableStateOf("") }
    var confirmPassword by remember { mutableStateOf("") }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(NibblerColors.DarkBackground)
            .padding(16.dp),
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .widthIn(max = 440.dp)
                .fillMaxWidth()
                .background(NibblerColors.DarkSurfaceContainer)
                .border(1.dp, NibblerColors.DarkOutlineThin, RoundedCornerShape(8.dp))
        ) {
            Column(
                modifier = Modifier.padding(28.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .size(32.dp)
                            .background(NibblerColors.BauhausRed),
                        contentAlignment = Alignment.Center
                    ) {
                        Text("N", fontFamily = FontFamily.SansSerif, fontWeight = FontWeight.Bold, color = Color.White, fontSize = 18.sp)
                    }
                    Spacer(modifier = Modifier.width(12.dp))
                    Column {
                        Text(
                            text = "Nibbler",
                            fontFamily = FontFamily.SansSerif,
                            fontWeight = FontWeight.Bold,
                            fontSize = 24.sp,
                            color = NibblerColors.BauhausCoral
                        )
                        Text(
                            text = "Reset your password",
                            fontFamily = FontFamily.SansSerif,
                            fontSize = 9.sp,
                            fontWeight = FontWeight.Bold,
                            color = NibblerColors.DarkOnSurfaceVariant,
                            letterSpacing = 0.08.sp
                        )
                    }
                }

                Spacer(modifier = Modifier.height(20.dp))
                Box(modifier = Modifier.fillMaxWidth().height(1.dp).background(NibblerColors.DarkOutlineThin))
                Spacer(modifier = Modifier.height(20.dp))

                when (flow.step) {
                    ResetStep.EMAIL -> {
                        Text(
                            text = "Enter your email address and we'll send you a code to reset your password.",
                            color = NibblerColors.DarkOnSurfaceVariant,
                            fontSize = 13.sp,
                            modifier = Modifier.fillMaxWidth()
                        )
                        Spacer(modifier = Modifier.height(16.dp))
                        OutlinedTextField(
                            value = flow.email,
                            onValueChange = { flow.email = it; flow.errorMessage = null },
                            label = { Text("Email", fontFamily = FontFamily.SansSerif, fontSize = 11.sp) },
                            singleLine = true,
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                    ResetStep.CHECK_EMAIL -> {
                        Text(
                            text = "We've sent a reset link to your email. Open it and click the link to set a new password.",
                            color = NibblerColors.DarkOnSurfaceVariant,
                            fontSize = 13.sp,
                            modifier = Modifier.fillMaxWidth()
                        )
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            text = "The link expires in 10 minutes.",
                            color = NibblerColors.DarkOnSurfaceVariant,
                            fontSize = 12.sp,
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                    ResetStep.CODE -> {
                        if (token == null) {
                            Text(
                                text = "Enter the 6-digit code from your email and choose a new password.",
                                color = NibblerColors.DarkOnSurfaceVariant,
                                fontSize = 13.sp,
                                modifier = Modifier.fillMaxWidth()
                            )
                            Spacer(modifier = Modifier.height(16.dp))
                            OutlinedTextField(
                                value = code,
                                onValueChange = { code = it; flow.errorMessage = null },
                                label = { Text("Reset code", fontFamily = FontFamily.SansSerif, fontSize = 11.sp) },
                                singleLine = true,
                                shape = RoundedCornerShape(8.dp),
                                modifier = Modifier.fillMaxWidth()
                            )
                            Spacer(modifier = Modifier.height(16.dp))
                        } else {
                            Text(
                                text = "Choose a new password for your account.",
                                color = NibblerColors.DarkOnSurfaceVariant,
                                fontSize = 13.sp,
                                modifier = Modifier.fillMaxWidth()
                            )
                            Spacer(modifier = Modifier.height(16.dp))
                        }
                        OutlinedTextField(
                            value = newPassword,
                            onValueChange = { newPassword = it; flow.errorMessage = null },
                            label = { Text("New password", fontFamily = FontFamily.SansSerif, fontSize = 11.sp) },
                            visualTransformation = PasswordVisualTransformation(),
                            singleLine = true,
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier.fillMaxWidth()
                        )
                        Spacer(modifier = Modifier.height(16.dp))
                        OutlinedTextField(
                            value = confirmPassword,
                            onValueChange = { confirmPassword = it; flow.errorMessage = null },
                            label = { Text("Confirm password", fontFamily = FontFamily.SansSerif, fontSize = 11.sp) },
                            visualTransformation = PasswordVisualTransformation(),
                            singleLine = true,
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                    ResetStep.SUCCESS -> {
                        Text(
                            text = "Your password has been reset successfully. You can now log in with your new password.",
                            color = NibblerColors.DarkOnSurfaceVariant,
                            fontSize = 13.sp,
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }

                if (flow.errorMessage != null) {
                    Spacer(modifier = Modifier.height(12.dp))
                    Text(
                        text = flow.errorMessage!!,
                        color = NibblerColors.Error,
                        fontFamily = FontFamily.SansSerif,
                        fontSize = 11.sp
                    )
                }

                Spacer(modifier = Modifier.height(24.dp))

                when (flow.step) {
                    ResetStep.EMAIL -> {
                        Button(
                            onClick = { flow.submitEmail() },
                            shape = RoundedCornerShape(8.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = NibblerColors.BauhausCoral,
                                contentColor = Color.White
                            ),
                            enabled = !flow.isLoading,
                            modifier = Modifier.fillMaxWidth().height(48.dp)
                        ) {
                            if (flow.isLoading) {
                                CircularProgressIndicator(color = Color.White, strokeWidth = 2.dp, modifier = Modifier.size(20.dp))
                            } else {
                                Text("Send reset code", fontFamily = FontFamily.SansSerif, fontWeight = FontWeight.Bold, fontSize = 13.sp, letterSpacing = 0.05.sp)
                            }
                        }
                    }
                    ResetStep.CHECK_EMAIL -> {
                        TextButton(
                            onClick = { flow.enterCodeManually() },
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Text("Enter code manually", fontFamily = FontFamily.SansSerif, fontSize = 11.sp, color = NibblerColors.Accent)
                        }
                    }
                    ResetStep.CODE -> {
                        Button(
                            onClick = { flow.submitReset(code, newPassword, confirmPassword) },
                            shape = RoundedCornerShape(8.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = NibblerColors.BauhausCoral,
                                contentColor = Color.White
                            ),
                            enabled = !flow.isLoading,
                            modifier = Modifier.fillMaxWidth().height(48.dp)
                        ) {
                            if (flow.isLoading) {
                                CircularProgressIndicator(color = Color.White, strokeWidth = 2.dp, modifier = Modifier.size(20.dp))
                            } else {
                                Text("Reset password", fontFamily = FontFamily.SansSerif, fontWeight = FontWeight.Bold, fontSize = 13.sp, letterSpacing = 0.05.sp)
                            }
                        }
                    }
                    ResetStep.SUCCESS -> {
                        Button(
                            onClick = { flow.backToLogin() },
                            shape = RoundedCornerShape(8.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = NibblerColors.BauhausCoral,
                                contentColor = Color.White
                            ),
                            modifier = Modifier.fillMaxWidth().height(48.dp)
                        ) {
                            Text("Back to login", fontFamily = FontFamily.SansSerif, fontWeight = FontWeight.Bold, fontSize = 13.sp, letterSpacing = 0.05.sp)
                        }
                    }
                }

                if (flow.step != ResetStep.SUCCESS) {
                    TextButton(
                        onClick = { flow.backToLogin() },
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text("Back to login", fontFamily = FontFamily.SansSerif, fontSize = 11.sp, color = NibblerColors.DarkOnSurfaceVariant)
                    }
                }
            }
        }
    }
}
